import { ItemTypeCategoryOrder } from '@/utils/ItemTypes';
import {
  DepositClearingDataEntry,
  InvoiceDataInput,
  getInvoiceData,
} from './InvoiceDataCalculation';

const itemTypeCategory = {
  CONTAINER: 0,
  CONTAINER_STATION: 1,
  ASSET: 2,
};

export async function generateInvoiceInfoReport(
  data: InvoiceDataInput,
  filterUnusedContainersAndItemLocations = false,
) {
  const invoicingData = await getInvoiceData(data);

  if (invoicingData == null || Object.keys(invoicingData).length === 0) {
    return new Blob(['No data to report'], { type: 'text/plain' });
  }

  const csvData: any[] = [];

  const headerRow = [
    'Location',
    'Behälter Typ Id',
    'Behälter Typ Name',
    'Ausgegeben',
    'Zurückgenommen (Benutzt)',
    'Zurückgenommen (Unbenutzt)',
    'Differenz',
  ];

  csvData.push(headerRow);

  const totals: Record<
    string,
    Omit<DepositClearingDataEntry, 'tooFewReturned' | 'tooManyReturned'>
  > = {};

  for (const locationName in invoicingData) {
    const locationData = invoicingData[locationName];

    // Filter out unused container types if requested
    const filteredLocationData = filterUnusedContainersAndItemLocations
      ? locationData.filter(
          (containerData) =>
            containerData.delivered !== 0 ||
            containerData.returnDirty !== 0 ||
            containerData.returnClean !== 0,
        )
      : locationData;

    // Only add location if it has containers after filtering
    if (filteredLocationData.length > 0) {
      csvData.push([locationName]);

      for (const containerData of filteredLocationData) {
        const row = [
          '',
          containerData.itemTypeId,
          containerData.itemTypeName,
          containerData.delivered,
          containerData.returnDirty,
          containerData.returnClean,
          containerData.total,
        ];
        csvData.push(row);

        // A container type id and an item type id can be the same number, so the
        // category has to be part of the key.
        const totalsKey = `${containerData.itemTypeCategory}-${containerData.itemTypeId}`;
        if (!totals[totalsKey]) {
          totals[totalsKey] = {
            itemTypeCategory: containerData.itemTypeCategory,
            itemTypeId: containerData.itemTypeId,
            itemTypeImage: containerData.itemTypeImage,
            itemTypeName: containerData.itemTypeName,
            delivered: 0,
            returnDirty: 0,
            returnClean: 0,
            total: 0,
          };
        }
        totals[totalsKey].delivered += containerData.delivered;
        totals[totalsKey].returnDirty += containerData.returnDirty;
        totals[totalsKey].returnClean += containerData.returnClean;
        totals[totalsKey].total += containerData.total;
      }
      csvData.push([]);
    }
  }

  // Filter out unused container types from totals if requested
  if (filterUnusedContainersAndItemLocations) {
    Object.keys(totals).forEach((itemId) => {
      const containerTotal = totals[itemId];
      if (
        containerTotal.delivered === 0 &&
        containerTotal.returnDirty === 0 &&
        containerTotal.returnClean === 0
      ) {
        delete totals[itemId];
      }
    });
  }

  // Add the totals to csvData only if there are any
  if (Object.keys(totals).length > 0) {
    csvData.push(['Total']);
    const sortedTotals = Object.values(totals).sort((a, b) => {
      if (
        ItemTypeCategoryOrder[a.itemTypeCategory] !==
        ItemTypeCategoryOrder[b.itemTypeCategory]
      ) {
        return (
          ItemTypeCategoryOrder[a.itemTypeCategory] -
          ItemTypeCategoryOrder[b.itemTypeCategory]
        );
      }
      return a.itemTypeId - b.itemTypeId;
    });
    for (const total of sortedTotals) {
      csvData.push([
        '',
        total.itemTypeId,
        total.itemTypeName,
        total.delivered,
        total.returnDirty,
        total.returnClean,
        total.total,
      ]);
    }
  }

  const csv = csvData.map((r) => r.join(';')).join('\n');
  return new Blob([csv], { type: 'text/csv' });
}
